import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useTheme, useMediaQuery, TextField, InputAdornment, Button, Box, LinearProgress, Typography, Paper, Tabs, Tab } from '@mui/material';
import { useContext, useEffect, useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { AuthContext, useApi, API, IconContext } from '@plumeuk/shapeshift-identity';
import 'x2js';

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useCourses = (showProgress, data) => {
    const [apiResult, getData, setCourses] = useApi(data ? null : API.courses(showProgress ?? true), data);
    const getCourses = (showProgressOveride, configOverride) => {
        const _showProgress = showProgressOveride ?? showProgress;
        getData(configOverride
            ? { ...configOverride, url: API.courses(_showProgress ?? true) }
            : API.courses(_showProgress ?? true));
    };
    useEffect(() => data && setCourses(data), [data]);
    return { courses: apiResult.data, apiResult, getCourses, setCourses };
};

const CoursesDataProvider = ({ children, showProgress, data }) => {
    const dataHook = useCourses(showProgress, data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle courses" });
};

const useStyles$5 = makeStyles()((theme) => ({
    searchBox: {
        boxSizing: "border-box",
        borderRadius: "6px",
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
        margin: "10px 0"
    }
}));
const SearchBox = ({ className, InputProps, ...props }) => {
    const { classes, cx } = useStyles$5();
    const { searchIcon } = useContext(IconContext);
    return (jsx(TextField, { className: cx(classes.searchBox, className), ...props, id: "search-box", "data-test-id": "search-input", variant: "standard", name: "search-box", type: "text", InputProps: {
            disableUnderline: true,
            placeholder: "Search",
            startAdornment: (jsx(InputAdornment, { position: "start", children: searchIcon })),
            ...InputProps
        } }));
};

const useStyles$4 = makeStyles()((theme) => ({
    outlineButton: {
        border: `${theme.palette.secondary.main} 1px solid`,
        color: theme.palette.secondary.main,
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main + "10"
        }
    }
}));
const OutlineButton = ({ children, className, ...props }) => {
    const { classes, cx } = useStyles$4();
    return (jsx(Button, { className: cx(classes.outlineButton, className), ...props, children: children }));
};

const useStyles$3 = makeStyles()((theme) => ({
    loadingContainer: {
        display: "flex",
        flexGrow: 2,
        [theme.breakpoints.down("sm")]: {
            padding: "5px 20px",
            flexDirection: "row"
        },
        [theme.breakpoints.up("sm")]: {
            padding: "25px 25px 15px 35px",
            flexDirection: "column",
            justifyContent: "center",
            borderTop: "3px solid #d1d1d150"
        }
    },
    loadingBar: {
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            flexGrow: 2,
            flexDirection: "row",
            margin: "8px 15px 15px 0"
        }
    },
    percentCompleted: {
        fontSize: "15px"
    }
}));
const CourseLoadingBar = ({ className, percentCompleted, ...props }) => {
    const { classes, cx } = useStyles$3();
    const mq = useMq();
    if (percentCompleted === 100)
        return jsx(Fragment, {});
    return (jsxs(Box, { className: cx(className, classes.loadingContainer), ...props, children: [jsx(LinearProgress, { variant: "determinate", className: classes.loadingBar, value: percentCompleted }), jsxs(Typography, { className: classes.percentCompleted, variant: "body1", children: [jsxs("span", { children: [percentCompleted, "%"] }), !mq.mobile && " complete"] })] }));
};

const useStyles$2 = makeStyles()((theme, { isComplete }) => ({
    courseItem: {
        cursor: isComplete ? "pointer" : "inherit",
        marginBottom: "25px"
    },
    paper: {
        display: "flex",
        borderRadius: "10px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    },
    imageContainer: {
        overflow: "hidden",
        "& img": {
            objectFit: "cover",
            width: "100%",
            height: "100%",
            borderRadius: "10px 0 0 10px",
            [theme.breakpoints.down("sm")]: {
                borderRadius: "10px 10px 0 0"
            }
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "300px"
        },
        [theme.breakpoints.up("sm")]: {
            width: "250px",
            height: "230px",
            minWidth: "250px",
            maxWidth: "250px"
        }
    },
    infoContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px 35px",
        flexGrow: 3,
        "& h4": {
            marginBottom: "15px"
        },
        [theme.breakpoints.down("sm")]: {
            padding: "20px"
        }
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 3,
        "& > div:first-of-type": {
            display: "flex",
            flexGrow: 2,
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
            }
        }
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px",
        [theme.breakpoints.down("sm")]: {
            padding: "0 20px 20px 20px"
        },
        "& > button": {
            textWrap: "no-wrap",
            margin: "10px 0",
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                margin: "5px 0"
            }
        }
    },
    certificateBtn: {
        backgroundColor: "white",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    },
    courseNavBtn: {
        paddingRight: "20px",
        "& svg": {
            marginLeft: "6px"
        }
    }
}));
const CourseItem = ({ className, enrolled, course, onClick, onGoToCourseAction, onGoToCertificateAction, ...props }) => {
    const { getUrl } = useFileBaseUrl();
    const mq = useMq();
    const isComplete = course.percentCompleted === 100;
    const { classes, cx } = useStyles$2({ isComplete });
    const { chevronRightIcon } = useContext(IconContext);
    const handleOnClick = useCallback((e) => {
        onClick?.(e);
        if (isComplete)
            return;
        onGoToCourseAction();
    }, [onGoToCourseAction, onClick, isComplete]);
    const getSmallImage = (e) => {
        return e?.formats?.small?.url ?? e.url;
    };
    const dataTestId = `course-item-${course.id}`;
    return (jsx(Box, { "data-test-id": dataTestId, className: cx(classes.courseItem, className), onClick: handleOnClick, ...props, children: jsxs(Paper, { className: classes.paper, children: [jsx(Box, { className: classes.imageContainer, children: course.featuredImage && jsx("img", { src: getUrl(getSmallImage(course.featuredImage)) }) }), jsxs(Box, { className: classes.contentContainer, children: [jsxs("div", { children: [jsxs(Box, { className: classes.infoContainer, children: [jsx(Typography, { variant: "h4", children: course.title }), (course.instructor || course.author) && jsxs(Typography, { variant: "body1", children: ["by ", course.instructor ?? course.author] })] }), (enrolled !== false && mq.mobile) && jsx(CourseLoadingBar, { percentCompleted: course.percentCompleted }), jsxs(Box, { className: classes.buttonContainer, children: [jsxs(Button, { "data-test-id": `${dataTestId}-nav-action-btn`, className: classes.courseNavBtn, onClick: () => onGoToCourseAction(), children: [isComplete ? "Go to course" : course.percentCompleted === 0 ? "Start" : "Resume", chevronRightIcon] }), isComplete &&
                                            jsx(OutlineButton, { "data-test-id": `${dataTestId}-view-certificate-btn`, onClick: () => onGoToCertificateAction(), className: classes.certificateBtn, children: "View Certificate" })] })] }), (enrolled !== false && !mq.mobile) && jsx(CourseLoadingBar, { percentCompleted: course.percentCompleted })] })] }) }));
};

const useStyles$1 = makeStyles()((theme) => ({
    tabs: {
        [theme.breakpoints.down("sm")]: {
            display: "inherit",
            width: "100%"
        }
    },
    tab: {
        textTransform: "uppercase",
        color: theme.palette.text.primary + " !important"
    }
}));
const CourseTableTabs = ({ className, onChange, tabSelected, ...props }) => {
    const { classes, cx } = useStyles$1();
    const mq = useMq();
    const handleChange = (_event, newValue) => {
        onChange(newValue);
    };
    return (jsx(Box, { className: cx(classes.tabs, className), ...props, children: jsxs(Tabs, { variant: mq.mobile ? "fullWidth" : "standard", value: tabSelected, onChange: handleChange, "aria-label": "shapeshift courses tabs", children: [jsx(Tab, { className: classes.tab, value: "progress", label: "In Progress" }), jsx(Tab, { className: classes.tab, value: "complete", label: "Completed" })] }) }));
};

const useStyles = makeStyles()((theme) => ({
    courseTable: {},
    topContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "16px",
        [theme.breakpoints.down("sm")]: {
            display: "inherit"
        }
    },
    title: {
        fontSize: "30px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "18px",
            fontWeight: 400
        }
    },
    noCoursesBody: {
        marginTop: "30px"
    },
    noCoursesForFiltersBody: {
        marginTop: "30px"
    }
}));
const CourseTable = ({ className, data, onGoToCourseAction, onGoToCertificateAction, children, ...props }) => {
    const { classes, cx } = useStyles();
    const [tabSelected, setTab] = useState("progress");
    const [searchTerm, setSearchTerm] = useState("");
    const mq = useMq();
    const applyFilters = (courses) => courses
        ?.filter(e => (e.percentCompleted === 100) === (tabSelected === "complete")
        && e.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) ?? [];
    return (jsxs(Box, { className: cx(classes.courseTable, className), ...props, children: [jsxs(Box, { className: classes.topContainer, children: [jsx(Typography, { variant: "h4", className: classes.title, children: "My courses" }), mq.mobile && jsx(SearchBox, { onChange: (e) => setSearchTerm(e.target.value) }), jsx(CourseTableTabs, { tabSelected: tabSelected, onChange: e => setTab(e) })] }), jsx(CoursesDataProvider, { data: data, children: ({ courses, apiResult }) => (jsxs(Fragment, { children: [children?.(data, apiResult) ?? applyFilters(courses)
                            .map((course, i) => jsx(CourseItem, { "data-test-id": `course_item_${course.id}`, course: course, onGoToCourseAction: () => onGoToCourseAction(course.slug), onGoToCertificateAction: () => onGoToCertificateAction(course.slug) }, "Course-item-" + i)), !children?.(data, apiResult) && courses?.length === 0 &&
                            jsx(Typography, { className: classes.noCoursesBody, children: "You're not enrolled into any courses, visit the library" }), !children?.(data, apiResult) && (courses?.length ?? 0) > 0 && applyFilters(courses).length === 0 &&
                            jsx(Typography, { className: classes.noCoursesForFiltersBody, children: "No courses found matching filters" })] })) })] }));
};

export { CourseItem, CourseLoadingBar, CourseTable };
